import React, { useEffect, useState } from "react";
import { NMAxiosInst } from "@neumetric/blueprint-lib/store";
import {
  NMStatsTable,
  BarChart,
  DonutChart,
  Col,
} from "@neumetric/blueprint-lib/components";
import { ExportSection } from "../ExportSection";

const MODULE_KEY = "Investigator_Severity_vs_Incident_Status";

export const InvestigatorSeverityStats = (props) => {
  const { user, Columns, dateRange, dateRangeList, mode, onDataLoaded } = props;
  const priorities = React.useMemo(
    () => props.priorities || ["Emergency", "Urgent", "Critical", "High", "Medium", "Low", "Minimal", "Least"],
    [props.priorities]
  );
  const [responseData, setResponseData] = useState(null);
  const [tableData, setTableData] = useState([]);
  const dateRangeEnums = dateRangeList || [];

  const effectiveRangeId =
    typeof dateRange === "string" ? dateRange : dateRange?.id || "last_365_days";

  const getDataFromServer = async (rangeId, isCurrentRequest) => {
    const payload = { date_range_type: rangeId };
    let res = await NMAxiosInst.post("/incidents/severity_dashboard", payload);
    if (isCurrentRequest()) setResponseData({ rangeId, data: res });
  };

  useEffect(() => {
    if (!responseData || responseData.rangeId !== effectiveRangeId) return;
    let tempTableData = getDataFromResponse(responseData.data, user, priorities);
    if (tempTableData) {
      setTableData([...tempTableData]);
    }
    onDataLoaded?.(responseData.rangeId);
  }, [responseData, effectiveRangeId, user, priorities, onDataLoaded]);

  useEffect(() => {
    if (!effectiveRangeId) return;
    let cancelled = false;
    getDataFromServer(effectiveRangeId, () => !cancelled).catch((error) => {
      if (!cancelled) {
        console.error("Error fetching investigator severity stats:", error);
        setResponseData({ rangeId: effectiveRangeId, data: {} });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [effectiveRangeId]);

  // helper to read a display name from "label" that can be a string or {id,label}
  const nameOf = (v) => (typeof v === "object" && v !== null ? v.label ?? v.id ?? String(v) : String(v));

  const chartDatasets = React.useMemo(() => {
    const brand = user.org.brandname;
    const n = (v) => (v === "-" || v == null ? 0 : Number(v));

    // Keep stack/legend order stable
    const SERIES_ORDER = ["OTHER", "OPEN", "STARTED", "CLOSED"];

    // Map stack label -> blueprint color key
    const COLOR_KEY = {
      OTHER: "INVALID",
      OPEN: "PENDING",
      STARTED: "STARTED",
      CLOSED: "COMPLETE",
    };

    // rows excluding brand row
    const rows = (tableData || []).filter((r) => {
      const lbl = nameOf(r.label);
      return lbl && lbl !== brand;
    });

    // severityName (string) -> row
    const bySev = new Map(rows.map((r) => [nameOf(r.label), r]));

    const declaredSeverities = (priorities || []).map((s) => nameOf(s)).filter(Boolean);

    // Always use all declared priorities in given order
    const axisSeverities = declaredSeverities.length > 0 ? declaredSeverities : Array.from(bySev.keys());

    // Build datasets for priorities with non-zero values, filtering out those with all zeros
    return Object.fromEntries(
      axisSeverities
        .map((sevName) => {
          const r = bySev.get(sevName) || {};
          const values = {
            OTHER: n(r.OTHER),
            OPEN: n(r.OPEN),
            STARTED: n(r.STARTED),
            CLOSED: n(r.COMPLETE),
          };
          return {
            sevName,
            values,
            dataset: SERIES_ORDER.map((label) => ({
              key: COLOR_KEY[label],
              label,
              value: values[label] || 0, // ensure missing values become 0
            })),
          };
        })
        .filter(({ values }) => {
          // Only include severities with at least one non-zero value
          return Object.values(values).some((value) => value !== 0);
        })
        .map(({ sevName, dataset }) => [sevName, dataset])
    );
  }, [tableData, priorities, user.org.brandname]);

  const dateRangeLabel =
    dateRange?.label ??
    dateRangeEnums.find((o) => o.id === (typeof dateRange === "string" ? dateRange : dateRange?.id))?.label ??
    "";

  // ---- Donut (overall) numbers for Incident: Severity-wise ----
  const safeNum = (v) => (v === "-" || v == null ? 0 : Number(v));

  const sevBrandRow = tableData?.[0] ?? null;
  const sevTotalIncidents = sevBrandRow ? safeNum(sevBrandRow.total) : 0;
  const sevOtherIncidents = sevBrandRow ? safeNum(sevBrandRow.OTHER) : 0;
  const sevClosedIncidents = sevBrandRow ? safeNum(sevBrandRow.COMPLETE) : 0;

  const effectiveTotal = sevTotalIncidents - sevOtherIncidents;

  const sevPercent = sevBrandRow && effectiveTotal > 0 ? Math.round((sevClosedIncidents * 100) / effectiveTotal) : 0;

  const sevDonutDatasets = sevBrandRow
    ? [
        {
          key: "COMPLETE",
          value: safeNum(sevBrandRow.COMPLETE),
          label: "Closed",
          sortOrder: 4,
          legendOrder: 1,
          textColor: "#FFF",
        },
        {
          key: "STARTED",
          value: safeNum(sevBrandRow.STARTED),
          label: "Started",
          border: "true",
          sortOrder: 3,
          legendOrder: 2,
        },
        {
          key: "OPEN",
          value: safeNum(sevBrandRow.OPEN),
          label: "Open",
          sortOrder: 2,
          legendOrder: 3,
          textColor: "#FFF",
        },
        {
          key: "OTHER",
          value: safeNum(sevBrandRow.OTHER),
          label: "Other",
          border: "true",
          sortOrder: 1,
          legendOrder: 4,
          textColor: "#FFF",
        },
      ]
    : [
        { key: "COMPLETE", value: null, label: "Closed" },
        { key: "STARTED", value: null, label: "Started", border: "true" },
        { key: "OPEN", value: null, label: "Open" },
        { key: "OTHER", value: null, label: "Other", border: "true" },
      ];

  const donutDatasetToTable = (allDonutSets = []) => {
    const totals = {
      COMPLETE: 0,
      STARTED: 0,
      OPEN: 0,
      OTHER: 0,
    };
    allDonutSets.forEach((d) => {
      const key = d.key?.toUpperCase();
      if (key in totals) totals[key] += Number(d.value) || 0;
    });

    const DonutColumns = [
      { title: "Closed", attribute: "COMPLETE" },
      { title: "Started", attribute: "STARTED" },
      { title: "Open", attribute: "OPEN" },
      { title: "Other", attribute: "OTHER" },
      { title: "Closure (%)", attribute: "CLOSURE" },
    ];
    const DonutRows = [
      {
        COMPLETE: totals.COMPLETE,
        STARTED: totals.STARTED,
        OPEN: totals.OPEN,
        OTHER: totals.OTHER,
        CLOSURE: `${sevPercent}%`,
      },
    ];

    return { DonutColumns, DonutRows };
  };
  const { DonutColumns, DonutRows } = donutDatasetToTable(sevDonutDatasets);

  return (
    <>
      {(mode === "fusion" || mode === "export_everything") && (
        <>
          {/* Donut header */}
          <div
            style={{
              display: "flex",
              width: "100%",
              alignItems: "flex-start",
              flexWrap: "wrap",
              padding: "0 0 0 10px",
            }}
          >
            <ExportSection
              moduleKey={MODULE_KEY}
              index={1}
              exportType={mode === "export_everything" ? "chart" : "table"}
              exportData={{
                title: "Incident",
                columns: DonutColumns,
                rows: DonutRows,
              }}
              style={{ display: "inline-block", width: 350, flex: "0 0 auto" }}
            >
              <Col style={{ flex: "1 1 0", width: 350 }}>
                <div style={{ width: "100%", height: 200 }}>
                  <DonutChart
                    chartId="investigator-severity-donut"
                    title="Incident"
                    subtitle="Incident:"
                    totalCount={String(sevTotalIncidents || "-")}
                    classes={{ root: "h-200-px" }}
                    datasets={sevDonutDatasets}
                    metric={{
                      label: "Closure",
                      value: sevPercent,
                      type: "percent",
                    }}
                  />
                </div>
              </Col>
            </ExportSection>
          </div>
        </>
      )}

      {/* Only render table + bar chart if NOT in fusion mode */}
      {(mode === "full" || mode === "export_everything") && (
        <div
          style={{
            display: "flex",
            width: "100%",
            gap: "20px",
            alignItems: "flex-start",
            flexWrap: "wrap",
            padding: "20px 40px 20px 40px",
          }}
        >
          {/* Table */}
          <ExportSection
            moduleKey={MODULE_KEY}
            index={mode === "export_everything" ? 2 : 1}
            dateRangeLabel={dateRange?.label}
            exportType="table"
            exportData={{
              title: "Incident - Severity-wise",
              columns: Columns,
              rows: tableData,
            }}
            style={{
              flex: "1 1 48%",
              minWidth: 400,
              maxWidth: "50%",
              paddingTop: "0px",
            }}
          >
            <NMStatsTable
              id="Investigator_Severity_vs_Incident_Status"
              title="Incident"
              subTitle="Severity-wise"
              columns={Columns}
              dataSource={tableData}
              dateRange={dateRangeLabel}
              showDateInHeader={true}
              getCellTypeRepresentation={(cellInfo) => {
                const dotColumns = ["STARTED", "COMPLETE", "OPEN", "OTHER"];
                if (cellInfo.rowId === -1 && dotColumns.includes(cellInfo.columnId)) {
                  return "DOT";
                }
                if (cellInfo.columnId === "label" && cellInfo.rowId > 0) {
                  return "DOT";
                }
                return null;
              }}
            />
          </ExportSection>

          {/* Chart */}
          <ExportSection
            moduleKey={MODULE_KEY}
            index={mode === "export_everything" ? 3 : 2}
            dateRangeLabel={dateRange?.label}
            exportType="chart"
            style={{
              flex: "1 1 48%",
              minWidth: 400,
              maxWidth: "50%",
              paddingTop: 0,
              overflow: "visible",
            }}
          >
            <div className="chart-export-pad" style={{ width: "100%", overflow: "visible" }}>
              <div style={{ width: "100%", height: 420, paddingTop: "10px" }}>
                <BarChart
                  id="Investigator_Severity_vs_Incident_Status"
                  title="Incident"
                  subtitle="Severity-wise"
                  showDateInHeader={true}
                  label={{ xAxis: "Incidents", yAxis: "Severity" }}
                  datasets={chartDatasets}
                  stackType="normal"
                  dateRange={dateRangeLabel}
                  classes={{ root: "h-420-px w-100" }}
                />
              </div>
            </div>
          </ExportSection>
        </div>
      )}
    </>
  );
};

const getDataFromResponse = (res, user, priorities) => {
  if (!res || !res.severity_status || Object.keys(res.severity_status).length === 0) return [];

  const STATUS_CATEGORY_MAP = {
    complete: "COMPLETE",
    closed: "COMPLETE",
    failed: "COMPLETE",

    started: "STARTED",
    review: "STARTED",

    pending: "OPEN",
    open: "OPEN",
  };

  const severity_status = res.severity_status;
  const brandname = user.org.brandname;

  // Always include ALL priorities
  const allSeverities = priorities || [];
  let severityStats = [];

  let totalRow = {
    label: brandname,
    max_aging: 0,
    avg_aging: 0,
    total: 0,
    COMPLETE: 0,
    STARTED: 0,
    OPEN: 0,
    OTHER: 0,
    highlight: true,
  };

  let totalMaxAge = 0;
  let totalAvgAge = 0;

  for (let severity of allSeverities) {
    const severityRow = severity_status[severity] || {};
    const statuses = severityRow.statuses || {};

    const row = {
      label: {
        id: severity,
        label: severity,
      },
      avg_aging: severityRow.avg_aging || 0,
      max_aging: severityRow.max_aging || 0,
      total: statuses.total || 0,
      COMPLETE: 0,
      STARTED: 0,
      OPEN: 0,
      OTHER: 0,
      labelClassName: `bg${severity}`,
    };

    // Categorize each status into 4 fields
    for (let statusKey in statuses) {
      if (statusKey === "total") continue;
      const mappedCategory = STATUS_CATEGORY_MAP[statusKey.toLowerCase()] || "OTHER";
      row[mappedCategory] += statuses[statusKey];
    }

    // accumulate into totalRow
    totalMaxAge = Math.max(totalMaxAge, row.max_aging || 0);
    totalAvgAge += row.avg_aging || 0;

    row.max_aging = row.max_aging > 0 ? `${row.max_aging} d` : row.max_aging;
    row.avg_aging = row.avg_aging > 0 ? `${row.avg_aging} d` : row.avg_aging;

    // Only push to severityStats if row has non-zero values
    const numericFields = ["COMPLETE", "STARTED", "OPEN", "OTHER", "total"];
    const hasNonZeroValues = numericFields.some((field) => row[field] !== 0);

    if (hasNonZeroValues) {
      totalRow.total += row.total;
      totalRow.COMPLETE += row.COMPLETE;
      totalRow.STARTED += row.STARTED;
      totalRow.OPEN += row.OPEN;
      totalRow.OTHER += row.OTHER;

      severityStats.push(row);
    }
  }

  // divisor only considers non-empty rows
  const nonZeroSeverityStats = filterNonZeroRows(severityStats);
  let divisor = nonZeroSeverityStats.length || 1;

  totalRow.max_aging = totalMaxAge > 0 ? `${totalMaxAge} d` : totalMaxAge;
  totalRow.avg_aging = parseInt(totalAvgAge / divisor);
  totalRow.avg_aging = totalRow.avg_aging > 0 ? `${totalRow.avg_aging} d` : totalRow.avg_aging;

  const finalRows = [totalRow, ...severityStats];
  return convertZeroToHyphen(finalRows);
};

function filterNonZeroRows(rows) {
  return rows.filter((row) => {
    for (let key in row) {
      if (
        ["max_aging", "avg_aging", "total", "closed", "started", "open", "invalid"].includes(key) &&
        row[key] !== 0 &&
        !Number.isNaN(row[key]) &&
        row[key] !== "0"
      ) {
        return true;
      }
    }
    return false;
  });
}
function convertZeroToHyphen(rows) {
  rows.forEach((row) => {
    Object.keys(row).forEach((columnKey) => {
      if (row[columnKey] === 0 || row[columnKey] === "0") {
        row[columnKey] = "-";
      }
    });
  });
  return rows;
}
